import { createApi, fetchBaseQuery } from "@reduxjs/toolkit/query/react";

const API_BASE_URL = process.env.NEXT_PUBLIC_API_BASE_URL;

export interface OrderProduct {
  productId: string;
  quantity: number;
  productInfo?: {
    _id: string;
    name: string;
    featuredImg?: string;
    description?: {
      name: string;
    };
    price?: number;
  };
  productType?: string;
  status?: "pending" | "processing" | "shipped" | "delivered" | "cancelled";
}

export interface OrderUser {
  _id: string;
  name: string;
  email: string;
}

export interface Order {
  _id: string;
  trackingNumber: string;
  orderBy: OrderUser;
  orderInfo: OrderProduct[];
  totalAmount: number;
  status:
    | "pending"
    | "fulfilled"
    | "shipped"
    | "delivered"
    | "cancelled"
    | "processing";
  createdAt: string;
  updatedAt: string;
  paymentInfo?: any;
  shippingAddress?: any;
}

export interface OrderResponse {
  success: boolean;
  data: Order[] | { result: Order[]; meta: any };
  meta?: {
    page: number;
    limit: number;
    total: number;
    totalPage: number;
  };
}

export const ordersApi = createApi({
  reducerPath: "ordersApi",
  baseQuery: fetchBaseQuery({
    baseUrl: API_BASE_URL,
    prepareHeaders: (headers) => {
      // You might need to add auth token header here if it's stored in localStorage or Redux state
      // Currently relying on 'credentials: include' for cookies if that works for your backend
      // But for safety, let's assume standard token header if available
      const token = localStorage.getItem("accessToken");
      if (token) {
        headers.set("Authorization", `Bearer ${token}`);
      }
      return headers;
    },
    credentials: "include", // Ensure cookies are sent
  }),
  tagTypes: ["Orders"],
  endpoints: (builder) => ({
    getOrders: builder.query<
      OrderResponse,
      {
        page?: number;
        limit?: number;
        searchTerm?: string;
        dateFilter?: "daily" | "weekly" | "monthly" | "yearly" | "all";
        sortBy?: string;
        sortOrder?: "asc" | "desc";
        startDate?: string;
        endDate?: string;
      }
    >({
      query: (params) => {
        return {
          url: "/order",
          params: {
            page: params.page,
            limit: params.limit,
            searchTerm: params.searchTerm || undefined,
            dateFilter:
              params.dateFilter && params.dateFilter !== "all"
                ? params.dateFilter
                : undefined,
            sortBy: params.sortBy || undefined,
            sortOrder: params.sortOrder || undefined,
            startDate: params.startDate || undefined,
            endDate: params.endDate || undefined,
          },
        };
      },
      providesTags: ["Orders"],
    }),
    getOrderById: builder.query<{ success: boolean; data: Order }, string>({
      query: (id) => `/order/${id}`,
      providesTags: (result, error, id) => [{ type: "Orders", id }],
    }),
    updateOrderStatus: builder.mutation<
      { success: boolean; data: Order },
      { id: string; status: string }
    >({
      query: ({ id, status }) => ({
        url: `/order/status/${id}`,
        method: "PATCH",
        body: { status },
      }),
      invalidatesTags: ["Orders"],
    }),
    updateOrderItemStatus: builder.mutation<
      { success: boolean; data: Order },
      { orderId: string; productId: string; status: string }
    >({
      query: ({ orderId, productId, status }) => ({
        url: `/order/item-status/${orderId}/${productId}`,
        method: "PATCH",
        body: { status },
      }),
      invalidatesTags: ["Orders"],
    }),
    deleteOrder: builder.mutation<{ success: boolean }, string>({
      query: (id) => ({
        url: `/order/${id}`,
        method: "DELETE",
      }),
      invalidatesTags: ["Orders"],
    }),
  }),
});

export const {
  useGetOrdersQuery,
  useGetOrderByIdQuery,
  useUpdateOrderStatusMutation,
  useUpdateOrderItemStatusMutation,
  useDeleteOrderMutation,
} = ordersApi;
